import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Grid2,
  TextField,
  Typography,
} from '@mui/material';
import { Paper } from 'components';

import { makeStyles } from '@mui/styles';
import styles from './styles';
import { useIntl } from 'react-intl';

import { ReactComponent as IconSVG } from './i.svg';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import getToken from 'helpers/auth';

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DataGrid } from '@mui/x-data-grid';
import { Download, ShareButton } from 'components/Download';
import Swal from 'sweetalert2';
import { DownloadForOffline } from '@mui/icons-material';

const useStyles = makeStyles(styles);

export default function Current() {
  const classes = useStyles();
  const intl = useIntl();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [showStartDatePop, setShowStartDatePop] = useState(false);
  const [showEndDatePop, setShowEndDatePop] = useState(false);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [dataElectric, setDataElectric] = useState([]);
  const [stats, setStats] = useState({});
  const [statsImbalance, setStatsImbalance] = useState([]);
  const [timeLabel, setTimeLabel] = useState({ min: true, hour: false });
  const [blocking, setBlocking] = useState(false);
  const concentrator_id = useSelector(
    (state) => state.chargeDashboard.generalConsumes.concentrator_id
  );
  const charge = useSelector(
    (state) => state.chargeDashboard.generalConsumes.charge_info
  );
  const charge_id = charge ? charge.id : 0;
  const chartRef = useRef();

  const calculateStats = (arrDataFase) => {
    if (arrDataFase.length === 0) return { min: 0, max: 0, avg: 0 };
    const values = arrDataFase.map((item) => item[1]);
    const min = Math.min(...values).toFixed(2);
    const max = Math.max(...values).toFixed(2);
    const avg = (
      values.reduce((sum, value) => sum + value, 0) / values.length
    ).toFixed(2);
    return { min, max, avg };
  };

  const calculateStatsImbalance = (data) => {
    if (data.length === 0) return { avg: 0 };
    const values = data.map((item) => item[1]);
    const avg = (
      values.reduce((sum, value) => sum + value, 0) / values.length
    ).toFixed(2);
    return { avg };
  };

  useEffect(() => {
    filter5MinRecordsElectric(startDate, endDate);
  }, [timeLabel]);

  const set5MinDates = (data) => {
    let arrDataFase1 = [];
    let arrDataFase2 = [];
    let arrDataFase3 = [];
    let relativeImbalance = [];
    let seriesData = [];
    let statsImbalance = {};
    let stats = {};
    if (timeLabel.hour) {
      const hourlyData = {};

      data.forEach((record) => {
        const recordDate = record.date.toString();
        const { data: dataList, relative_imbalance: imbalance } = record;

        if (!hourlyData[recordDate]) {
          hourlyData[recordDate] = {
            fase1: [],
            fase2: [],
            fase3: [],
            imbalance: [],
          };
        }

        dataList.forEach(({ value }, index) => {
          if (value[0] !== undefined) {
            hourlyData[recordDate].fase1.push(parseFloat(value[0]));
          }
          if (value[1] !== undefined) {
            hourlyData[recordDate].fase2.push(parseFloat(value[1]));
          }
          if (value[2] !== undefined) {
            hourlyData[recordDate].fase3.push(parseFloat(value[2]));
          }
          if (imbalance && imbalance[index] !== undefined) {
            hourlyData[recordDate].imbalance.push(parseFloat(imbalance[index]));
          }
        });
      });

      for (const recordDate in hourlyData) {
        const { fase1, fase2, fase3, imbalance } = hourlyData[recordDate];
        const year = parseInt(recordDate.substring(0, 4));
        const month = parseInt(recordDate.substring(4, 6)) - 1;
        const day = parseInt(recordDate.substring(6, 8));
        const hour = parseInt(recordDate.substring(8));
        const date = new Date(year, month, day, hour);
        const milisecs = date.getTime();

        arrDataFase1.push([
          milisecs,
          fase1.length > 0 ? Math.max(...fase1) : null,
        ]);
        arrDataFase2.push([
          milisecs,
          fase2.length > 0 ? Math.max(...fase2) : null,
        ]);
        arrDataFase3.push([
          milisecs,
          fase3.length > 0 ? Math.max(...fase3) : null,
        ]);
        relativeImbalance.push([
          milisecs,
          imbalance.length > 0 ? Math.max(...imbalance) : null,
        ]);
      }

      seriesData = [
        {
          name: `${intl.formatMessage({ id: 'app.deepview.current' })}(A) 1`,
          data: arrDataFase1,
          yAxis: 0,
          color: '#333',
          marker: { enabled: false },
          lineWidth: 3,
        },
        arrDataFase2.length && {
          name: `${intl.formatMessage({ id: 'app.deepview.current' })}(A) 2`,
          data: arrDataFase2,
          yAxis: 0,
          color: '#FF4A4A',
          marker: { enabled: false },
          lineWidth: 3,
        },
        arrDataFase3.length && {
          name: `${intl.formatMessage({ id: 'app.deepview.current' })}(A) 3`,
          data: arrDataFase3,
          yAxis: 0,
          color: '#00AFEF',
          marker: { enabled: false },
          lineWidth: 3,
        },
        arrDataFase3.length && {
          name: `${intl.formatMessage({ id: 'app.deepview.unbalance' })}`,
          data: relativeImbalance,
          yAxis: 1,
          color: '#FF964F',
          marker: { enabled: false },
          lineWidth: 1,
        },
      ].filter(Boolean);

      stats = {
        fase1: calculateStats(arrDataFase1),
      };

      if (arrDataFase2.length) {
        stats.fase2 = calculateStats(arrDataFase2);
      }

      if (arrDataFase3.length) {
        stats.fase3 = calculateStats(arrDataFase3);
      }

      statsImbalance = {
        imbalance: calculateStatsImbalance(relativeImbalance),
      };
    } else {
      data.forEach((record) => {
        const recordDate = record.date.toString(); // Asegurar que recordDate es una cadena de texto
        const { data: dataList, relative_imbalance: imbalance } = record;

        const year = parseInt(recordDate.substring(0, 4));
        const month = parseInt(recordDate.substring(4, 6)) - 1;
        const day = parseInt(recordDate.substring(6, 8));
        const hour = parseInt(recordDate.substring(8));

        dataList.forEach(({ min, value }, index) => {
          const date = new Date(year, month, day, hour, min);
          const milisecs = date.getTime();

          if (value[0] !== undefined) {
            arrDataFase1.push([milisecs, parseFloat(value[0])]);
          }
          if (value[1] !== undefined) {
            arrDataFase2.push([milisecs, parseFloat(value[1])]);
          }
          if (value[2] !== undefined) {
            arrDataFase3.push([milisecs, parseFloat(value[2])]);
          }

          if (imbalance && imbalance[index] !== undefined) {
            relativeImbalance.push([milisecs, parseFloat(imbalance[index])]);
          }
        });
      });

      seriesData = [
        {
          name: `${intl.formatMessage({ id: 'app.deepview.current' })}(A) 1`,
          data: arrDataFase1,
          yAxis: 0,
          color: '#333',
          marker: { enabled: false },
          lineWidth: 3,
        },
        arrDataFase2.length && {
          name: `${intl.formatMessage({ id: 'app.deepview.current' })}(A) 2`,
          data: arrDataFase2,
          yAxis: 0,
          color: '#FF4A4A',
          marker: { enabled: false },
          lineWidth: 3,
        },
        arrDataFase3.length && {
          name: `${intl.formatMessage({ id: 'app.deepview.current' })}(A) 3`,
          data: arrDataFase3,
          yAxis: 0,
          color: '#00AFEF',
          marker: { enabled: false },
          lineWidth: 3,
        },
        arrDataFase3.length && {
          name: `${intl.formatMessage({ id: 'app.deepview.unbalance' })}`,
          data: relativeImbalance,
          yAxis: 1,
          color: '#FF964F',
          marker: { enabled: false },
          lineWidth: 1,
        },
      ].filter(Boolean);

      stats = {
        fase1: calculateStats(arrDataFase1),
      };

      if (arrDataFase2.length) {
        stats.fase2 = calculateStats(arrDataFase2);
      }

      if (arrDataFase3.length) {
        stats.fase3 = calculateStats(arrDataFase3);
      }

      statsImbalance = {
        imbalance: calculateStatsImbalance(relativeImbalance),
      };
    }

    setStats(stats);
    setStatsImbalance(statsImbalance);
    setDataElectric(seriesData);
  };

  const handleTime = (event) => {
    const { name, checked } = event.target;
    if (name === 'min' && checked) {
      setTimeLabel({
        min: true,
        hour: false,
      });
    } else if (name === 'hour' && checked) {
      setTimeLabel({
        min: false,
        hour: true,
      });
    }
  };

  const filter5MinRecordsElectric = (
    fechaIni,
    fechaFin,
    concentrador_id,
    charge_id
  ) => {
    let dayDifs = getDateDiffInDays(fechaIni, fechaFin);
    if (dayDifs > 31) {
      setErrorMessage(
        '[' +
          dayDifs +
          ' ' +
          intl.formatMessage({
            id: 'app.deepview.consumption.datesRangeError.rangeTooLong',
          })
      );
      return;
    } else {
      if (dayDifs < 0) {
        setErrorMessage(
          intl.formatMessage({
            id: 'app.deepview.consumption.datesRangeError.wrongIniEnd',
          })
        );
        return;
      }
      setErrorMessage(null);
    }
    let txtIniDate = getDateAsString(fechaIni);
    let txtEndDate = getDateAsString(fechaFin);
    txtIniDate = txtIniDate + '00';
    txtEndDate = txtEndDate + '23';
    dispatch(
      get5MinRecordsElectric(
        txtIniDate,
        txtEndDate,
        concentrador_id,
        charge_id,
        set5MinDates
      )
    );
  };

  const get5MinRecordsElectric = (startDate, endDate) => {
    return async () => {
      axios
        .post(
          'https://api.metimur.mx/api/charge/5minutalcorrienteData',
          {
            concentrator_id: concentrator_id,
            charge_id: charge_id,
            start_date: startDate,
            end_date: endDate,
          },
          {
            headers: { Authorization: 'Bearer ' + getToken() },
          }
        )
        .then((response) => {
          set5MinDates(response.data.data);
        })
        .catch(function (error) {
          console.error('Error API: ' + error);
        });
    };
  };

  function getFileName(disposition) {
    let filename = null;
    if (disposition && disposition.indexOf('attachment') !== -1) {
      var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      var matches = filenameRegex.exec(disposition);
      if (matches != null && matches[1]) {
        filename = matches[1].replace(/['"]/g, '');
      }
    }

    return filename;
  }

  const get5MinRecordsElectricDownload = (startDate, endDate) => {
    setBlocking(true);
    return async () => {
      try {
        const response = await axios.post(
          'https://api.metimur.mx/api/charge/5minutalcorrienteData',
          {
            concentrator_id: concentrator_id,
            charge_id: charge_id,
            start_date: `${getDateAsString(startDate)}00`,
            end_date: `${getDateAsString(endDate)}23`,
            export: true,
          },
          {
            headers: { Authorization: 'Bearer ' + getToken() },
            responseType: 'arraybuffer',
          }
        );
        const typeApp =
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        const downloadUrl = window.URL.createObjectURL(
          new Blob([response.data], { type: typeApp })
        );
        const link = document.createElement('a');
        const fileName =
          getFileName(response.headers['content-disposition']) ||
          'reporte.xlsx';
        link.href = downloadUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        console.log(error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error?.message || 'No se pudo descargar el archivo.',
        });
      }
      setBlocking(false);
    };
  };

  const getDateDiffInDays = (startDate, endDate) => {
    const diffTime = endDate - startDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays + 1;
  };

  const getDateAsString = (date) => {
    let year = date.getFullYear();
    let month = date.getMonth() + 1;
    let day = date.getDate();

    let strMonth = month.toString();
    if (month < 10) {
      strMonth = '0' + strMonth;
    }
    let strDay = day.toString();
    if (day < 10) {
      strDay = '0' + strDay;
    }

    let strDate = year + strMonth + strDay;
    return strDate;
  };

  const handleClickOpen = () => {
    setOpen(true);
    filter5MinRecordsElectric(startDate, endDate);
  };

  const handleClickClose = () => {
    setOpen(false);
    setTimeLabel({ min: true, hour: false });
  };

  const handleSelectEndDate = (finDate) => {
    if (open) {
      setEndDate(finDate);
      setShowEndDatePop(false);
      filter5MinRecordsElectric(startDate, finDate);
    } else {
      setEndDate(finDate);
      setShowEndDatePop(false);
      filter5MinRecordsElectric(startDate, finDate);
    }
  };

  const handleSelectStartDate = (iniDate) => {
    setStartDate(iniDate);
    setShowStartDatePop(false);
    setDataElectric([]);
    setErrorMessage(null);
    filter5MinRecordsElectric(iniDate, endDate);
  };

  const optionsElectric = {
    turboThreshold: 0,
    time: {
      useUTC: false,
      timezone: 'America/Mexico_City',
    },
    credits: {
      enabled: false,
    },
    title: {
      text: intl.formatMessage({ id: 'app.deepview.electric.chart.title' }),
    },
    xAxis: {
      type: 'datetime',
      crosshair: true,
      dateTimeLabelFormats: {
        millisecond: '%H:%M:%S.%L',
        second: '%H:%M:%S',
        minute: '%H:%M',
        hour: '%H:%M',
        day: '%a %e. %b',
        week: '%e. %b',
        month: "%b '%y",
        year: '%Y',
      },
    },
    yAxis: [
      {
        title: {
          text: intl.formatMessage({
            id: 'app.deepview.electric.chart.amperes',
          }),
        },
        labels: {
          format: '{value}A',
        },
      },
      {
        title: {
          text: intl.formatMessage({
            id: 'app.deepview.electric.chart.percentage',
          }),
        },
        labels: {
          format: '{value}%',
        },
        opposite: true,
        lineColor: '#FF964F',
        lineWidth: '2',
      },
    ],
    rangeSelector: {
      enabled: true,
      selected: 4,
    },
    tooltip: {
      shared: true,
      positioner: function (labelWidth, labelHeight, point) {
        const chart = this.chart;
        let x = point.plotX + chart.plotLeft;
        let y = point.plotY + chart.plotTop - 100;

        if (x + labelWidth > chart.chartWidth) {
          x = chart.chartWidth - labelWidth - 10;
        }
        if (y < 0) {
          y = 50;
        }
        return { x: x, y: y };
      },
      formatter: function () {
        const date = new Date(this.x);
        const options = {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: 'numeric',
          minute: 'numeric',
          weekday: 'long',
        };

        const formattedDate = date
          .toLocaleString(intl.formatMessage({ id: 'app.locale' }), options)
          .replace(',', '');

        let s = `<b>${formattedDate}</b><br/>`;

        this.points.forEach((point) => {
          s += `<span style="color:${point.series.color}">\u25CF</span> ${
            point.series.name
          }: <b>${Highcharts.numberFormat(point.y, 2)}</b> ${
            point.series.yAxis.opposite ? '%' : 'A'
          }<br/>`;
        });

        return s;
      },
    },
    series: dataElectric,
    legend: {
      itemStyle: {
        fontSize: '16px',
        fontWeight: 'bold',
      },
    },
  };

  const columns2 = [
    {
      field: 'avg',
      headerName: `${intl.formatMessage({
        id: 'app.deepview.unbalance',
      })} ${intl.formatMessage({
        id: 'app.metersCards.card.voltageAve',
      })} (%)`,
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      resizable: false,
      cellClassName: 'custom-cell',
    },
  ];

  const rows2 = [
    {
      id: 1, // ID único necesario para DataGrid
      phase: intl.formatMessage({ id: 'app.deepview.unbalance' }),
      ...statsImbalance.imbalance,
    },
  ].filter(Boolean);

  const columns = [
    {
      field: 'phase',
      headerName: '',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      resizable: false,
      sortable: false,
      renderCell: (params) => (
        <span
          style={{
            color: params.value.color,
            fontSize: '1rem',
            fontWeight: '900',
          }}
        >
          {params.value.text}
        </span>
      ),
    },
    {
      field: 'min',
      headerName: intl.formatMessage({ id: 'app.metersCards.card.voltageMin' }),
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      resizable: false,
      cellClassName: 'voltage-min',
    },
    {
      field: 'max',
      headerName: intl.formatMessage({ id: 'app.metersCards.card.voltageMax' }),
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      resizable: false,
      cellClassName: 'voltage-max',
    },
    {
      field: 'avg',
      headerName: intl.formatMessage({ id: 'app.metersCards.card.voltageAve' }),
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      resizable: false,
      cellClassName: 'voltage-avg',
    },
  ];

  const rows = [
    {
      id: 1,
      phase: {
        text: `${intl.formatMessage({ id: 'app.deepview.current' })}(A) 1`,
        color: '#333',
      },
      ...stats.fase1,
    },
    stats.fase2 && {
      id: 2,
      phase: {
        text: `${intl.formatMessage({ id: 'app.deepview.current' })}(A) 2`,
        color: '#FF4A4A',
      },
      ...stats.fase2,
    },
    stats.fase3 && {
      id: 3,
      phase: {
        text: `${intl.formatMessage({ id: 'app.deepview.current' })}(A) 3`,
        color: '#00AFEF',
      },
      ...stats.fase3,
    },
  ].filter(Boolean);

  return (
    <Box>
      <Paper className={classes.root} onClick={handleClickOpen}>
        <Box>
          <div className={classes.content}>
            <div>
              <Typography
                variant="h6"
                sx={{ fontWeight: '700', fontSize: '1rem', color: '#fff' }}
              >
                {intl.formatMessage({ id: 'app.deepview.electric.btnOpen' })}
              </Typography>
            </div>
            <div className={classes.iconWrapper}>
              <IconSVG className={classes.icon} />
            </div>
          </div>
          <div className={classes.footer}>
            <Typography variant="caption" className={classes.caption}>
              {intl.formatMessage({
                id: 'app.deepview.electric.btnOpen.datesMsg',
              })}
            </Typography>
          </div>
        </Box>
      </Paper>
      <Dialog fullWidth maxWidth="xl" onClose={handleClickClose} open={open}>
        <DialogTitle
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Chip
            sx={{
              fontWeight: 700,
              backgroundColor: '#730065',
              fontSize: '16px',
              color: 'white',
              marginBottom: '25px',
            }}
            label={intl.formatMessage({ id: 'app.deepview.electric.title' })}
          />
          <Box display="flex" gap={1}>
            <Download
              itemRef={chartRef}
              name={intl.formatMessage({
                id: 'app.deepview.electric.title',
              })}
            />
            <ShareButton
              itemRef={chartRef}
              name={intl.formatMessage({
                id: 'app.deepview.electric.title',
              })}
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="flex-start"
              >
                <Typography
                  className={classes.title}
                  style={{ color: 'red' }}
                  variant="h5"
                >
                  {errorMessage}
                </Typography>
                <Typography variant="h5">
                  {' '}
                  {intl.formatMessage({
                    id: 'app.deepview.electric.datesMsg',
                  })}{' '}
                </Typography>
              </Box>
              <Button
                type="button"
                variant="contained"
                color="secondary"
                startIcon={!blocking ? <DownloadForOffline /> : null}
                loading={blocking}
                loadingPosition="start"
                onClick={() => {
                  get5MinRecordsElectricDownload(startDate, endDate)();
                }}
              >
                {!blocking
                  ? intl.formatMessage({
                      id: 'app.button.downloadReport.title',
                    })
                  : intl.formatMessage({
                      id: 'app.button.downloadReport.loading',
                    })}
              </Button>
            </Box>
            <Grid2
              sx={{
                paddingTop: '1rem',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Grid2 display="flex" flexDirection="row" gap={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    format="dd/MM/yyyy"
                    value={startDate}
                    onChange={(date) => handleSelectStartDate(date)}
                    open={showStartDatePop}
                    onOpen={() => setShowStartDatePop(true)}
                    onClose={() => setShowStartDatePop(false)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        onFocus={() => setShowStartDatePop(true)}
                        sx={{ marginBottom: '10px', marginRight: '10px' }}
                      />
                    )}
                  />

                  <DatePicker
                    format="dd/MM/yyyy"
                    value={endDate}
                    onChange={(date) => {
                      setEndDate(date);
                      handleSelectEndDate(date);
                    }}
                    open={showEndDatePop}
                    onOpen={() => setShowEndDatePop(true)}
                    onClose={() => setShowEndDatePop(false)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        onFocus={() => setShowEndDatePop(true)}
                        sx={{ marginBottom: '10px', marginRight: '10px' }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid2>
              <Grid2>
                <FormGroup row={true}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={timeLabel.min}
                        color="secondary"
                        name="min"
                        onChange={handleTime}
                      />
                    }
                    label={intl.formatMessage({ id: 'app.charge.board.min' })}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={timeLabel.hour}
                        color="secondary"
                        name="hour"
                        onChange={handleTime}
                      />
                    }
                    label={intl.formatMessage({ id: 'app.charge.board.hour' })}
                  />
                </FormGroup>
              </Grid2>
            </Grid2>
          </DialogContentText>
          <Box ref={chartRef}>
            <Paper>
              <HighchartsReact
                highcharts={Highcharts}
                options={optionsElectric}
              />
            </Paper>
            <Grid2
              container={true}
              spacing={3}
              sx={{
                marginTop: '1rem',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Grid2
                size={{ sm: 6, xs: 12 }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'column',
                }}
              >
                <Chip
                  label={intl.formatMessage({
                    id: 'app.chargesCards.addCharge',
                  })}
                  sx={{
                    fontWeight: 700,
                    backgroundColor: '#730065',
                    fontSize: '16px',
                    color: 'white',
                    marginBottom: '25px',
                  }}
                />
                <DataGrid
                  rows={rows}
                  columns={columns}
                  hideFooter
                  disableColumnMenu
                  disableSelectionOnClick
                  sx={{
                    width: '100%',
                    boxShadow: 3,
                    '& .voltage-min': {
                      backgroundColor: '#8CBA42',
                      color: '#FFF',
                      textAlign: 'center',
                      fontWeight: '500',
                      fontSize: '.95em',
                    },
                    '& .voltage-max': {
                      backgroundColor: '#D64539',
                      color: '#FFF',
                      textAlign: 'center',
                      fontWeight: '500',
                      fontSize: '.95em',
                    },
                    '& .voltage-avg': {
                      backgroundColor: '#EAAB36',
                      color: '#FFF',
                      textAlign: 'center',
                      fontWeight: '500',
                      fontSize: '.95em',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      textAlign: 'center',
                      fontSize: '1rem',
                      fontWeight: '900',
                      minWidth: '25%',
                    },
                    '& .MuiDataGrid-cell': {
                      fontSize: 15,
                    },
                  }}
                />
              </Grid2>
              {stats.fase3 && (
                <Grid2
                  size={{ sm: 6, xs: 12 }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'column',
                  }}
                >
                  <Chip
                    label={intl.formatMessage({ id: 'app.deepview.unbalance' })}
                    sx={{
                      fontWeight: 700,
                      backgroundColor: '#730065',
                      fontSize: '16px',
                      color: 'white',
                      marginBottom: '25px',
                    }}
                  />
                  <DataGrid
                    rows={rows2}
                    columns={columns2}
                    hideFooter
                    disableColumnMenu
                    disableSelectionOnClick
                    sx={{
                      width: '100%',
                      '& .custom-cell': {
                        backgroundColor: '#FF964F',
                        color: '#FFF',
                        textAlign: 'center',
                        fontWeight: '500',
                        fontSize: '.95em',
                      },
                      '& .MuiDataGrid-columnHeaders': {
                        textAlign: 'center',
                        fontSize: '1em',
                        fontWeight: '900',
                        minWidth: '25%',
                      },
                      '& .MuiDataGrid-cell': {
                        fontSize: 15,
                      },
                    }}
                  />
                </Grid2>
              )}
            </Grid2>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            color="secondary"
            onClick={handleClickClose}
            variant="contained"
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
